import React, { useEffect } from 'react';
import { Grid, Box, Stack, Button, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import GenericDropdown from "../../pages/DeviceManagement/GenericDropdown";
import { CohortTabBar } from "../CMS/CohortTabBar";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useTranslation } from 'react-i18next';

interface Props {
  tabNames: string[];
  tabComponents?: React.ReactNode[];
  value: number;
  onTabChange: (newValue: number) => void;
  clinicOptions?: { value: string; label: string }[];
  selectedClinic?: string;
  clinicDropdownTitle?: string;
  onClinicChange?: (value: string) => void;
  filterOptions?: { value: number; label: string }[];
  selectedFilter?: string;
  filterDropdownTitle?: string;
  onFilterChange?: (value: string) => void;
  cohortList?: any[];
  selectedCohort?: any;
  onCohortChange?: (cohort: any) => void;
}

export default function CustomTabs({
  tabNames,
  tabComponents,
  value,
  onTabChange,
  filterOptions,
  selectedFilter,
  filterDropdownTitle,
  onFilterChange,
  clinicOptions,
  clinicDropdownTitle,
  selectedClinic,
  onClinicChange,
  cohortList,
  selectedCohort,
  onCohortChange,
}: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const state = (location as any).state as { startingTab?: number };
    if (state && state.startingTab !== undefined) {
      onTabChange(state.startingTab);
    }
  }, [location, onTabChange]);

  const handleClinicChange = (value: string) => {
    if (onClinicChange) {
      onClinicChange(value);
    }
  };

  const handleFilterChange = (value: string) => {
    if (onFilterChange) {
      onFilterChange(value);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        {/* Left side: Cohort selection buttons */}
        <Grid item xs>
          {cohortList && cohortList.length > 0 ? (
            <CohortTabBar
              cohortList={cohortList}
              activateCohort={onCohortChange || (() => {})}
              activeCohort={selectedCohort}
              refreshPatients={() => {}} // No longer needed in top navigation
            />
          ) : (
            <Box sx={{ height: 48 }} /> // Placeholder to maintain layout when no cohorts
          )}
        </Grid>

        {/* Right side: Dropdowns with spacing */}
        <Grid item>
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              onClick={() => navigate('/schedule')}
              color="primary"
              sx={{ textTransform: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
            >
              <CalendarMonthIcon />
              <Typography variant="caption">{t("my_schedule")}</Typography>
            </Button>
            {filterDropdownTitle && (
              <GenericDropdown
                options={filterOptions || []}
                selectedValue={selectedFilter || ''}
                onChange={handleFilterChange}
                title={filterDropdownTitle}
                sx={{ minWidth: 200 }}
              />
            )}
            {clinicDropdownTitle && (
              <GenericDropdown
                options={clinicOptions || []}
                selectedValue={selectedClinic || ''}
                onChange={handleClinicChange}
                title={clinicDropdownTitle}
                sx={{ minWidth: 200 }}
              />
            )}
          </Stack>
        </Grid>
      </Grid>
      {/* Since we only have CCM Central now, show it directly */}
      <Box sx={{ pt: 1 }}>
        {tabComponents && tabComponents[0]}
      </Box>
    </Box>
  );
}

// Tab helpers removed — tabs simplified in this layout
